import { create } from 'zustand';
import type { User, Session } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';

/**
 * Auth Store
 *
 * Manages Supabase authentication state.
 * Handles sign up, sign in, sign out, and password reset.
 */

interface AuthState {
  // Auth state
  user: User | null;
  session: Session | null;
  isLoading: boolean;
  error: string | null;

  // Actions
  initialize: () => Promise<void>;
  signUp: (email: string, password: string, displayName?: string) => Promise<{ error?: string }>;
  signIn: (email: string, password: string) => Promise<{ error?: string }>;
  signInWithGoogle: () => Promise<{ error?: string }>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error?: string }>;
  updateDisplayName: (displayName: string) => Promise<{ error?: string }>;
  clearError: () => void;
}

// Guard against double-initialization. `initialize()` is called from both the
// public-route Root (main.tsx) and AuthGuard, and React StrictMode double-invokes
// effects in dev — without this we'd register the `onAuthStateChange` listener
// (and its user-sync / beta-key claim) more than once.
let authInitialized = false;

// Helper to sync user to machina_users table
async function syncUserToDatabase(user: User) {
  try {
    const displayName = user.user_metadata?.display_name || user.email?.split('@')[0] || 'Player';

    await supabase
      .from('machina_users')
      .upsert({
        id: user.id,
        display_name: displayName,
      }, {
        onConflict: 'id',
      });
  } catch (err) {
    console.error('Failed to sync user to database:', err);
  }
}

export const useAuthStore = create<AuthState>()((set) => ({
  user: null,
  session: null,
  isLoading: true,
  error: null,

  initialize: async () => {
    if (authInitialized) return;
    authInitialized = true;
    try {
      // Get current session
      const { data: { session }, error } = await supabase.auth.getSession();

      if (error) {
        console.error('Auth init error:', error);
        set({ isLoading: false, error: error.message });
        return;
      }

      set({
        session,
        user: session?.user ?? null,
        isLoading: false,
      });

      // Listen for auth state changes
      supabase.auth.onAuthStateChange(async (_event, session) => {
        set({
          session,
          user: session?.user ?? null,
        });

        // Sync user to machina_users table on sign in
        if (session?.user) {
          syncUserToDatabase(session.user);

          // Claim pending beta key (from Google OAuth redirect)
          const pendingKey = sessionStorage.getItem('machina:pending-beta-key');
          if (pendingKey) {
            sessionStorage.removeItem('machina:pending-beta-key');
            try {
              // uid is derived from auth.uid() server-side; no user_id arg.
              const { data } = await supabase.rpc('machina_claim_beta_key', {
                p_code: pendingKey,
              });
              if (data === true) {
                console.log('[Auth] Beta key claimed successfully after OAuth');
              } else {
                console.warn('[Auth] Beta key claim failed after OAuth (may already be claimed)');
              }
            } catch (err) {
              console.warn('[Auth] Beta key claim error after OAuth:', err);
            }
          }
        }
      });
    } catch (err) {
      console.error('Auth initialization failed:', err);
      set({ isLoading: false });
    }
  },

  signUp: async (email, password, displayName) => {
    set({ isLoading: true, error: null });

    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            display_name: displayName || email.split('@')[0],
          },
        },
      });

      if (error) {
        set({ isLoading: false, error: error.message });
        return { error: error.message };
      }

      // If email confirmation is required, user will be null until confirmed
      if (data.user && !data.session) {
        set({ isLoading: false });
        return { error: 'Please check your email to confirm your account.' };
      }

      set({
        user: data.user,
        session: data.session,
        isLoading: false,
      });

      return {};
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Sign up failed';
      set({ isLoading: false, error: message });
      return { error: message };
    }
  },

  signIn: async (email, password) => {
    set({ isLoading: true, error: null });

    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        set({ isLoading: false, error: error.message });
        return { error: error.message };
      }

      set({
        user: data.user,
        session: data.session,
        isLoading: false,
      });

      return {};
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Sign in failed';
      set({ isLoading: false, error: message });
      return { error: message };
    }
  },

  signInWithGoogle: async () => {
    set({ isLoading: true, error: null });

    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}`,
        },
      });

      if (error) {
        set({ isLoading: false, error: error.message });
        return { error: error.message };
      }

      // OAuth redirects, so we don't set state here
      return {};
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Google sign in failed';
      set({ isLoading: false, error: message });
      return { error: message };
    }
  },

  signOut: async () => {
    set({ isLoading: true });

    try {
      await supabase.auth.signOut();
      set({
        user: null,
        session: null,
        isLoading: false,
        error: null,
      });
    } catch (err) {
      console.error('Sign out error:', err);
      set({ isLoading: false });
    }
  },

  resetPassword: async (email) => {
    set({ isLoading: true, error: null });

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (error) {
        set({ isLoading: false, error: error.message });
        return { error: error.message };
      }

      set({ isLoading: false });
      return {};
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Password reset failed';
      set({ isLoading: false, error: message });
      return { error: message };
    }
  },

  updateDisplayName: async (displayName) => {
    set({ isLoading: true, error: null });

    try {
      // Update Supabase Auth user metadata
      const { data, error } = await supabase.auth.updateUser({
        data: { display_name: displayName },
      });

      if (error) {
        set({ isLoading: false, error: error.message });
        return { error: error.message };
      }

      // Also update machina_users table
      if (data.user) {
        await supabase
          .from('machina_users')
          .upsert({
            id: data.user.id,
            display_name: displayName,
          }, {
            onConflict: 'id',
          });
      }

      set({ user: data.user, isLoading: false });
      return {};
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Update failed';
      set({ isLoading: false, error: message });
      return { error: message };
    }
  },

  clearError: () => set({ error: null }),
}));

// Helper to get user display name
export function getUserDisplayName(user: User | null): string {
  if (!user) return 'Guest';
  return user.user_metadata?.display_name || user.email?.split('@')[0] || 'Player';
}
